import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";
import { useWorkshopBlocker, FLOW } from "../../logic/useWorkshopBlocker";


type Option = {
  key: string;
  label_en: string;
  label_de: string;
};

const OPTIONS: Option[] = [
  { key: "stay_home", label_en: "I stay at home", label_de: "Ich bleibe zu Hause" },
  { key: "drive_somewhere", label_en: "I go somewhere", label_de: "Ich fahre irgendwo hin" },
  { key: "official_point", label_en: "I look for an official gathering point", label_de: "Ich suche einen offiziellen Sammelpunkt" },
  { key: "dont_know", label_en: "I don't know", label_de: "Ich weiß es nicht" },
];

const STORAGE_PREFIX = "crisisScenario";
const CACHE_TTL = 1000 * 60 * 30; // 30 min session

export default function ScenarioCrisis(props: { handlePageChange: (p: string) => void }) {
  const { handlePageChange } = props;
  const blocker = useWorkshopBlocker();
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [notice, setNotice] = React.useState<string | null>(null);

  const [selected, setSelected] = React.useState<string>("");
  const [submitted, setSubmitted] = React.useState(false);

  const [workshopId, setWorkshopId] = React.useState<string | null>(null);
  const [employeeId, setEmployeeId] = React.useState<string | null>(null);

  /* ---------------- INIT ---------------- */
  const myIndex = FLOW.indexOf("ScenarioCrisis");
  const blockerIndex = FLOW.indexOf(blocker);
  const canContinue = blockerIndex > myIndex;

  function handleContinue() {
    if (canContinue) {
      handlePageChange(FLOW[myIndex + 1]);
      return;
    }
    setNotice("ENG / DE");
  }

  const loadLocal = React.useCallback(
    (workshopId: string, employeeId: string, backendAnswer: string | null) => {
      const key = `${STORAGE_PREFIX}_${workshopId}_${employeeId}`;
      const saved = localStorage.getItem(key);

      if (!backendAnswer) {
        localStorage.removeItem(key);
        setSelected("");
        setSubmitted(false);
        return;
      }

      if (!saved) {
        setSelected(backendAnswer);
        setSubmitted(true);
        return;
      }

      try {
        const parsed = JSON.parse(saved);

        if (
          !parsed ||
          typeof parsed.selected !== "string" ||
          typeof parsed.timestamp !== "number"
        ) {
          localStorage.removeItem(key);
          setSelected(backendAnswer);
          setSubmitted(true);
          return;
        }

        const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;

        if (isExpired) {
          localStorage.removeItem(key);
          setSelected(backendAnswer);
          setSubmitted(true);
          return;
        }

        setSelected(parsed.selected);
        setSubmitted(true);
      } catch (e) {
        console.error("LOCAL PARSE ERROR", e);
        localStorage.removeItem(key);
        setSelected(backendAnswer);
        setSubmitted(true);
      }
    },
    []
  );

  const loadContext = React.useCallback(async () => {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) return;

    setEmployeeId(emp.id);
    setWorkshopId(workshop.id);

    const { data: existingAnswer, error } = await supabase
      .from("workshop_participant_gathering_decision")
      .select("answer_key")
      .eq("workshop_id", workshop.id)
      .eq("employee_id", emp.id)
      .maybeSingle();

    if (error) {
      console.error("LOAD CRISIS ANSWER ERROR:", error);
      return;
    }

    loadLocal(workshop.id, emp.id, existingAnswer?.answer_key ?? null);
  }, [loadLocal]);

  React.useEffect(() => {
    loadContext();
  }, [loadContext]);

  async function handleSubmit() {
    if (!selected || !workshopId || !employeeId) return;

    const localKey = `${STORAGE_PREFIX}_${workshopId}_${employeeId}`;

    localStorage.setItem(
      localKey,
      JSON.stringify({
        selected,
        timestamp: Date.now(),
      })
    );

    const { error } = await supabase
      .from("workshop_participant_gathering_decision")
      .upsert(
        {
          workshop_id: workshopId,
          employee_id: employeeId,
          answer_key: selected,
        },
        {
          onConflict: "workshop_id,employee_id",
        }
      );

    if (error) {
      console.error("UPSERT ERROR:", error);
      return;
    }

    setSubmitted(true);
  }

  function handleRedo() {
    if (workshopId && employeeId) {
      localStorage.removeItem(`${STORAGE_PREFIX}_${workshopId}_${employeeId}`);
    }

    setSelected("");
    setSubmitted(false);
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/crisis.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
        py: { xs: 3, sm: 5 },
      }}
    >
      <Paper
        sx={{
          width: "min(900px, 100%)",
          maxWidth: 900,
          p: { xs: 2.5, sm: 4, md: 5 },
          borderRadius: { xs: 3, sm: 5 },
          background: "linear-gradient(180deg, #311f1f, #221515)",
        }}
      >
        <Stack spacing={{ xs: 3, sm: 4 }}>

          {/* TITLE */}
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1.25rem", sm: "1.45rem", md: "1.6rem" } }}>
            {isDE ? "⚠️ KRISENLAGE IN DEINER STADT" : "⚠️ CRISIS SITUATION IN YOUR CITY"}
          </Typography>

          {/* DESCRIPTION */}
          <Typography sx={{ color: "#ccc", whiteSpace: "pre-line", fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            {isDE
              ? `In deiner Region kommt es zu einer akuten Lage:

• Teile der Infrastruktur fallen aus
• Bevölkerung wird informiert
• Hilfe wird organisiert

👉 Offizielle Empfehlung:
Suche einen Katastrophenschutz-Leuchtturm oder Sammelpunkt auf.`
              : `An acute situation is unfolding in your region:

• Parts of the infrastructure are failing
• The population is being informed
• Help is being organized

👉 Official recommendation:
Go to a civil protection lighthouse or gathering point.`}
          </Typography>

          {/* QUESTION */}
          <Typography sx={{ color: "#ff8a8a", fontWeight: 700 }}>
            {isDE
              ? "👉 Weißt du, wo der nächste ist?"
              : "👉 Do you know where the nearest one is?"}
          </Typography>

          <Typography sx={{ color: "#aaa" }}>
            {isDE ? "Wo würdest du hingehen?" : "Where would you go?"}
          </Typography>

          {/* OPTIONS */}
          {!submitted && (
            <RadioGroup
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              {OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  value={opt.key}
                  control={
                    <Radio
                      sx={{
                        color: "rgba(255,255,255,0.65)",
                        "&.Mui-checked": { color: "#ff4d4d" },
                      }}
                    />
                  }
                  label={isDE ? opt.label_de : opt.label_en}
                  sx={{
                    px: 2,
                    py: 1,
                    mb: 1,
                    borderRadius: 2,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.12)",

                    "& .MuiFormControlLabel-label": {
                      color: "#fff",
                      fontWeight: 500,
                    },
                  }}
                />
              ))}
            </RadioGroup>
          )}

          {submitted && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                background: "rgba(0,0,0,0.35)",
                border: "1px solid rgba(255,255,255,0.16)",
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "1.1rem",
                  fontWeight: 800,
                  lineHeight: 1.6,
                }}
              >
                {isDE
                  ? "Danke für deine Antwort. Bitte achte auf deine Anleitung für die nächsten Schritte."
                  : "Thank you for your answer. Please pay attention to your guide for the next steps."}
              </Typography>
            </Box>
          )}

          {/* ACTION */}
          {!submitted ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!selected}
              sx={{
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                fontWeight: 700,
                "&.Mui-disabled": {
                  background: "#777",
                  color: "rgba(255,255,255,0.75)",
                },
              }}
            >
              {isDE ? "Antwort speichern" : "Submit answer"}
            </Button>
          ) : (
            <Stack>
              <Button
                variant="outlined"
                onClick={handleRedo}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.4)",
                  fontWeight: 700,
                  "&:hover": {
                    borderColor: "#fff",
                    background: "rgba(255,255,255,0.05)",
                  },
                }}
              >
                {isDE ? "Antwort ändern" : "Change answer"}
              </Button>
              <Button
                variant="contained"
                onClick={handleContinue}
                sx={{
                  mt: 1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 800,
                  px: 3,
                  bgcolor: canContinue ? "#ffffff" : "rgba(255,255,255,0.35)",
                  color: canContinue ? "#111827" : "rgba(255,255,255,0.8)",
                  "&:hover": {
                    bgcolor: canContinue ? "#f3f4f6" : "rgba(255,255,255,0.35)",
                  },
                }}
              >
              {isDE ? "Weiter" : "Continue"}
            </Button>
          </Stack>
          )}
        </Stack>
      </Paper>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setNotice(null)} severity="info" sx={{ width: "100%", fontWeight: 700 }}>
          {isDE
            ? "Die Workshop-Leitung wird diesen Schritt freigeben, oder bitte beantworte die Frage vollständig und sende sie zuerst ab."
            : "The workshop holder will enable this step, or please complete and submit your answer first."}
        </Alert>
      </Snackbar>
    </Box>
  );
}